import React, { useState } from 'react';
import type { Debt } from '../types/debt';
import { addPayment } from '../services/debtService';
import { X, DollarSign, Calendar, AlignLeft } from 'lucide-react';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  debt: Debt | null;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, onSaved, debt }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !debt) return null;

  const remaining = debt.amount - debt.amountPaid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = Number(amount);
    if (!num || num <= 0 || num > remaining) return;
    setLoading(true);
    addPayment(debt.id, { amount: num, date, note: note.trim() || undefined });
    setLoading(false);
    setAmount(''); setNote('');
    onSaved();
    onClose();
  };

  const inputClass = "w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-gray-500 bg-white/5 border border-white/10 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative w-full max-w-sm rounded-2xl shadow-2xl fade-in-up" style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div>
            <h2 className="text-base font-semibold text-white">Record Payment</h2>
            <p className="text-xs text-gray-500 mt-0.5">{debt.creditorName} · {debt.currency} {remaining.toLocaleString(undefined, { minimumFractionDigits: 2 })} remaining</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={labelClass}>Payment Amount</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input id="paymentAmount" className={`${inputClass} pl-9`} type="number" step="0.01" min="0.01" max={remaining}
                placeholder={`Max ${remaining.toFixed(2)}`} value={amount} onChange={(e) => setAmount(e.target.value)} required />
            </div>
          </div>
          <div>
            <label className={labelClass}>Payment Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input className={`${inputClass} pl-9`} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>
          </div>
          <div>
            <label className={labelClass}>Note <span className="text-gray-600">(optional)</span></label>
            <div className="relative">
              <AlignLeft className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
              <textarea className={`${inputClass} pl-9 resize-none`} rows={2} placeholder="Payment note…" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 transition-colors">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 transition-colors shadow-lg shadow-indigo-500/25">
              {loading ? 'Saving…' : 'Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;
